import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Alert,
  Fade,
  Paper,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  AutoAwesome as AutoAwesomeIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  PlayArrow as PlayIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import InteractiveReasoningForm from '../components/InteractiveReasoning/InteractiveReasoningForm';

const InteractiveReasoningPage = () => {
  const theme = useTheme();
  const [error, setError] = useState(null);

  const features = [
    {
      icon: <AutoAwesomeIcon color="primary" />,
      title: "AI-Generated Plans",
      description: "Automatically generate structured reasoning plans from complex queries"
    },
    {
      icon: <VisibilityIcon color="secondary" />,
      title: "Plan Visualization",
      description: "See each step of the reasoning process before execution"
    },
    {
      icon: <EditIcon color="info" />,
      title: "Fully Editable",
      description: "Modify, reorder, add, or remove steps to customize the approach"
    },
    {
      icon: <PlayIcon color="success" />,
      title: "Step-by-Step Execution",
      description: "Watch as each step executes with real-time progress updates"
    },
    {
      icon: <TimelineIcon color="warning" />,
      title: "Dependency Management",
      description: "Steps can depend on previous results for complex workflows"
    }
  ];

  const exampleQueries = [
    "What are the latest evidence-based protocols for managing neonatal sepsis in preterm infants, considering antibiotic resistance patterns and long-term neurodevelopmental outcomes?",
    "Compare the efficacy and safety profiles of different surfactant preparations for respiratory distress syndrome, including cost-effectiveness analysis.",
    "Develop a comprehensive diagnostic and treatment plan for a neonate presenting with seizures, including differential diagnosis and monitoring protocols.",
    "Analyze the current research on optimal nutrition strategies for extremely low birth weight infants and their impact on growth and development."
  ];

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Fade in={true} timeout={800}>
        <Box>
          {/* Page Header */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
              <PsychologyIcon 
                sx={{ 
                  fontSize: 64, 
                  color: 'secondary.main',
                  filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.2))'
                }} 
              />
            </Box>
            
            <Typography 
              variant="h3" 
              component="h1" 
              fontWeight="bold" 
              gutterBottom
              sx={{
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              Interactive Reasoning
            </Typography>
            
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: 800, mx: 'auto' }}>
              Generate, review, and execute step-by-step reasoning plans for complex medical queries. 
              Take control of the AI's thinking process with full transparency and customization.
            </Typography>
          </Box>

          {error && (
            <Alert 
              severity="error" 
              sx={{ mb: 4, borderRadius: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          <Grid container spacing={4}>
            {/* Main Form */}
            <Grid item xs={12} lg={8}>
              <InteractiveReasoningForm onError={setError} />
            </Grid>

            {/* Sidebar */}
            <Grid item xs={12} lg={4}>
              {/* Features */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  mb: 3, 
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  ✨ Key Features
                </Typography>
                
                <List dense>
                  {features.map((feature, index) => (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {feature.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={feature.title}
                        secondary={feature.description}
                        primaryTypographyProps={{ fontWeight: 'medium', fontSize: '0.9rem' }}
                        secondaryTypographyProps={{ fontSize: '0.8rem' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>

              {/* Example Queries */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 3, 
                  borderRadius: 3,
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(66, 66, 66, 0.5)' : 'rgba(248, 249, 250, 0.8)',
                  backdropFilter: 'blur(8px)',
                  border: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Typography variant="h6" fontWeight="bold" gutterBottom>
                  💡 Example Queries
                </Typography>
                
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Try these complex queries to see interactive reasoning in action:
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {exampleQueries.map((query, index) => (
                    <Card 
                      key={index} 
                      variant="outlined" 
                      sx={{ 
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          boxShadow: 2,
                          transform: 'translateY(-2px)',
                          borderColor: theme.palette.primary.main
                        }
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: '0.85rem',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {query}
                        </Typography>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Paper>
            </Grid>
          </Grid>

          {/* How It Works */}
          <Box sx={{ mt: 6 }}>
            <Typography variant="h4" fontWeight="bold" textAlign="center" gutterBottom>
              How It Works
            </Typography>
            
            <Grid container spacing={3} sx={{ mt: 2 }}>
              {[
                {
                  step: "1",
                  title: "Enter Complex Query",
                  description: "Provide a detailed medical question that requires multi-step reasoning",
                  color: "primary"
                },
                {
                  step: "2", 
                  title: "Review Generated Plan",
                  description: "AI creates a structured plan with RAG searches, web searches, and synthesis steps",
                  color: "secondary"
                },
                {
                  step: "3",
                  title: "Customize & Modify",
                  description: "Edit queries, reorder steps, add new steps, or remove unnecessary ones",
                  color: "info"
                },
                {
                  step: "4",
                  title: "Execute & Monitor",
                  description: "Run the plan and watch real-time progress with detailed results for each step",
                  color: "success"
                }
              ].map((item, index) => (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <Card 
                    sx={{ 
                      height: '100%',
                      textAlign: 'center',
                      border: `2px solid ${theme.palette[item.color].main}`,
                      backgroundColor: `${theme.palette[item.color].main}10`
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          backgroundColor: `${item.color}.main`,
                          color: 'white',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '1.5rem',
                          fontWeight: 'bold',
                          mx: 'auto',
                          mb: 2
                        }}
                      >
                        {item.step}
                      </Box>
                      
                      <Typography variant="h6" fontWeight="bold" gutterBottom>
                        {item.title}
                      </Typography>
                      
                      <Typography variant="body2" color="text.secondary">
                        {item.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Fade>
    </Container>
  );
};

export default InteractiveReasoningPage;
